import * as THREE from 'three';
import { CRUMBLE_SECONDS, CRUMBLE_SINK } from './catalog';

// The collapse visual: when the support graph drops a piece to <= 0 it is detached from the build
// group into a transient crumble group and animated for ~1s — sink, slight tumble, fade out — then
// disposed. It is purely cosmetic: the logical state (occupancy slots, support graph, mirror arrays)
// is freed BEFORE the animation starts, so the freed spot is immediately legal to rebuild on, and
// the falling solid carries no collider (the player walks through it). Geometry is reused (never
// rebuilt per-frame); fading swaps in cloned transparent materials so the shared tier material is
// untouched, mirroring the ghost dispose discipline.

interface Crumbling {
  obj: THREE.Object3D;
  t: number;                       // elapsed seconds
  yaw0: number;
  yawDrift: number;
  pitchDrift: number;
  baseY: number;
  mats: THREE.Material[];          // the cloned fading materials we own + must dispose
}

export class CrumbleField {
  readonly group = new THREE.Group();
  private readonly active: Crumbling[] = [];

  // Take a placed solid (already removed from the logical state) and start its fall. The object is
  // reparented from `fromGroup` into the crumble group so it keeps its world transform.
  start(obj: THREE.Object3D, fromGroup: THREE.Object3D): void {
    fromGroup.remove(obj);
    this.group.add(obj);
    const mats: THREE.Material[] = [];
    obj.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      const src = mesh.material as THREE.Material | THREE.Material[];
      const clone = (m: THREE.Material): THREE.Material => {
        const c = m.clone();
        c.transparent = true;
        mats.push(c);
        return c;
      };
      mesh.material = Array.isArray(src) ? src.map(clone) : clone(src);
    });
    this.active.push({
      obj, t: 0, yaw0: obj.rotation.y,
      yawDrift: (Math.random() - 0.5) * 0.6,
      pitchDrift: (Math.random() - 0.5) * 0.5,
      baseY: obj.position.y, mats,
    });
  }

  // Advance every active crumble; finished ones dispose and drop out. Zero cost when idle.
  tick(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const c = this.active[i];
      c.t += dt;
      const k = Math.min(1, c.t / CRUMBLE_SECONDS);
      const ease = k * k; // sink accelerates a touch
      c.obj.position.y = c.baseY - CRUMBLE_SINK * ease;
      c.obj.rotation.y = c.yaw0 + c.yawDrift * k;
      c.obj.rotation.x = c.pitchDrift * k;
      const opacity = 1 - k;
      for (const m of c.mats) (m as THREE.Material & { opacity: number }).opacity = opacity;
      if (k >= 1) {
        this.dispose(c);
        this.active.splice(i, 1);
      }
    }
  }

  get busy(): boolean {
    return this.active.length > 0;
  }

  private dispose(c: Crumbling): void {
    this.group.remove(c.obj);
    c.obj.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
    });
    for (const m of c.mats) m.dispose();
  }
}
